//
// AHT Weight Management — Tirzepatide (Mounjaro®) Initial Supply Pathway
// Source: AHT_Mounjaro_Clinical_Pathway__1_.docx v1.4
//
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
// This pathway covers initial supply only: 1 × Mounjaro® 2.5mg KwikPen.

import type { Pathway } from "../../types/pathway.types";
import {
  PGD_FRESH_CHECKS,
  OUT_CANNOT_SUPPLY,
  tirzepatideScreeningDownstreamNodes,
  tirzepatideScreeningOutcomes,
} from "./_shared/tirzepatide-screening-downstream";

// ─────────────────────────────────────────────────────────────────────────────
// Node IDs
// ─────────────────────────────────────────────────────────────────────────────

const CONFIRM_CARD = "tzp-init-confirmation-card";

// ─────────────────────────────────────────────────────────────────────────────
// Pathway
// ─────────────────────────────────────────────────────────────────────────────

export const ahtWtTirzepatideInitialPathway: Pathway = {
  id: "aht-wt-tirzepatide-initial",
  name: "Tirzepatide (Mounjaro®) — Initial Supply",
  population: "Adults aged 18–84 attending face-to-face",
  pgdRefs: ["pgd-aht-wt-tirzepatide"],
  demographicConfiguration: { bannerFields: [] },
  entryNode: CONFIRM_CARD,

  nodes: [
    // ─────────────────────────────────────────────────────────────────────────
    // Step 1 — Confirm answers carried over from the selection pathway
    // ─────────────────────────────────────────────────────────────────────────
    {
      id: CONFIRM_CARD,
      type: "confirmation_card",
      heading: "Confirm answers from drug selection",
      items: [
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q3_thyroid",
          questionText: "Personal or family history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN2)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q4_pancreatitis",
          questionText: "History of pancreatitis (acute or chronic)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q3_thyroid_cancer_men2",
          sourceItemId: "q5_t1dm_t3c",
          questionText: "Type 1 diabetes (T1DM) or Type 3c (pancreatogenic) diabetes?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q7_severe_renal_or_hepatic_impairment",
          sourceItemId: "severe_renal",
          questionText: "Severe renal impairment (CrCl <15 mL/min)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q7_severe_renal_or_hepatic_impairment",
          sourceItemId: "severe_hepatic",
          questionText: "Severe hepatic impairment (Child-Pugh C)?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q14_severe_gi_or_gastroparesis",
          sourceItemId: "q14_severe_gi",
          questionText: "Severe or unstable gastrointestinal disease, including gastroparesis?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q15_pregnancy_breastfeeding_planning",
          questionText: "Pregnant, breastfeeding, or planning pregnancy within the next 6 months?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
        {
          sourceNodeId: "Q17_ciclosporin",
          sourceItemId: "q18_retinopathy",
          questionText: "Proliferative diabetic retinopathy, or diabetic retinopathy currently requiring active treatment?",
          safeAnswer: "no",
          cannotProceed: { outcomeId: OUT_CANNOT_SUPPLY },
        },
      ],
      next: PGD_FRESH_CHECKS,
    },

    // ─────────────────────────────────────────────────────────────────────────
    // Steps 2b–5 — shared screening downstream (PGD fresh checks, interaction
    // checks, cautions review, supply attestation). Single source of truth.
    // ─────────────────────────────────────────────────────────────────────────
    ...tirzepatideScreeningDownstreamNodes,
  ],

  outcomes: tirzepatideScreeningOutcomes,
};
